import { CloudFile } from 'owa-attachment-file-types';
import {
    closeLinkContextMenu,
    createLoadingLinkView,
    getComposeLinkViewState,
    isContextMenuOpen,
    removeAllComposeLinkViewStates,
    showLinkContextMenu,
} from 'owa-compose-link';
import { PluginUtils } from 'owa-editor';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getFileProviderType } from 'owa-fileprovider-link';
import { getGuid } from 'owa-guid';
import {
    AnchorElementsSource,
    getNumberOfSharingLinks,
    getSharingLinkInfo,
    RecipientContainer,
    SharingLinkInfo,
} from 'owa-link-data';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { format } from 'owa-string-utils';
import { trace } from 'owa-trace';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import {
    BeforePasteEvent,
    ChangeSource,
    ContentChangedEvent,
    ExtractContentEvent,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';
import { insertLinks } from '../insertLinksFromFilePicker/insertLinks';
import { LinkIdContainer } from '../schema/LinkIdContainer';
import { OUTLOOK_BEAUTIFIED_PREFIX } from '../utils/constants';
import getAllSharingLinks from '../utils/getAllSharingLinks';
import handleBeforePasteEvent from '../utils/handleBeforePasteEvent';
import { getSharingInfoToTryUpdateLink } from '../utils/handleOneLink/getSharingInfoToTryUpdateLink';
import isLinkRemovalRelatedEvent from '../utils/isLinkRemovalRelatedEvent';
import isReadonlyBeautifulLink from '../utils/isReadonlyBeautifulLink';
import { isSupportedProviderType } from '../utils/isSupportedProviderType';
import { processAllLinksInEditor } from '../utils/processAllLinksInEditor';
import processContentChangedEvent from '../utils/processContentChangedEvent';
import { processKeyDownEvent } from '../utils/processKeyDownEvent';
import { removeComposeOnlyContent } from '../utils/removeComposeOnlyContent';
import trySetIdAndClassName from '../utils/trySetIdAndClassName';

const RESIZE_EVENT_NAME = 'resize';

export interface ILinkPlugin extends EditorPlugin {
    insertLinksFromFilePicker: (files: CloudFile[]) => void;
}

let linkPluginCounter: number = 0;
export default class LinkPlugin implements ILinkPlugin {
    public editor: Editor;
    private utils: PluginUtils;
    private pendingSPLinksTask;
    private onCutDisposer: () => void;

    // This is for handling stuck spinner from Ctrl+Z undo
    // If we can't find the link Id in this array, we would remove the spinner on event contentChange from setContent.
    private linkIdsWithSpinner: string[];
    private id: number;
    private insertLinksTaskId: number;

    constructor(
        private linkIdContainer: LinkIdContainer,
        public getRecipientWells: () => RecipientContainer[],
        public fromAddress: string
    ) {
        this.linkIdsWithSpinner = [];
        this.id = ++linkPluginCounter;
        this.insertLinksTaskId = 0;
    }

    public getName() {
        return 'Link';
    }

    public initialize(editor: Editor): void {
        trace.info('LinkPlugin.initialize');
        this.editor = editor;
        this.onCutDisposer = this.editor.addDomEventHandler('cut', this.tryGetAllSPLinks);
        window.addEventListener(RESIZE_EVENT_NAME, this.onScroll);
    }

    public setPluginUtils(utils: PluginUtils) {
        this.utils = utils;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.Scroll) {
            this.onScroll();
        } else if (event.eventType === PluginEventType.BeforePaste) {
            // Handles pasting text on the editor (but not html blob)
            handleBeforePasteEvent(
                <BeforePasteEvent>event,
                this,
                this.linkIdContainer,
                this.linkIdsWithSpinner
            );
        } else if (event.eventType === PluginEventType.EditorReady) {
            trace.info('LinkPlugin.EditorReady');
            processAllLinksInEditor(
                this,
                this.linkIdContainer,
                this.linkIdsWithSpinner,
                false /* shouldChangeLinkId */,
                AnchorElementsSource.EditorLoadedOrDraftOrReply
            );
        } else if (event.eventType === PluginEventType.ContentChanged) {
            // Handles adding an hyperlink using the hyperlink button in compose or pasting html blob
            const contentChangedEvent: ContentChangedEvent = event as ContentChangedEvent;
            if (
                (contentChangedEvent.source === ChangeSource.AutoLink ||
                    contentChangedEvent.source === ChangeSource.CreateLink) &&
                contentChangedEvent.data &&
                contentChangedEvent.data instanceof HTMLAnchorElement
            ) {
                const providerType: AttachmentDataProviderType = getFileProviderType(
                    contentChangedEvent.data.href
                );
                if (!isSupportedProviderType(providerType)) {
                    return;
                }

                let anchorElementsSource: AnchorElementsSource = AnchorElementsSource.Unknown;
                if (contentChangedEvent.source === ChangeSource.AutoLink) {
                    anchorElementsSource = AnchorElementsSource.AutoLink;
                } else if (contentChangedEvent.source === ChangeSource.CreateLink) {
                    anchorElementsSource = AnchorElementsSource.InsertLinkFromToolbar;
                }
                trace.info('LinkPlugin.handleLink');
                trySetIdAndClassName(contentChangedEvent.data, true /*shouldChangeLinkId*/);
                getSharingInfoToTryUpdateLink(
                    this,
                    providerType,
                    contentChangedEvent.source === ChangeSource.AutoLink &&
                        providerType ===
                            AttachmentDataProviderType.OneDrivePro /* shouldBeautifyLink */,
                    true /* shouldRehydrate  */,
                    false /* isPreviouslyBeautified */,
                    contentChangedEvent.data,
                    this.linkIdContainer,
                    0 /* linkIndex */,
                    this.linkIdsWithSpinner,
                    anchorElementsSource
                );
            } else {
                // Handles loading from draft, reply, reply all and fwd, undo
                processContentChangedEvent(
                    event,
                    this,
                    this.linkIdContainer,
                    this.linkIdsWithSpinner
                );
            }
        } else if (event.eventType === PluginEventType.ExtractContent) {
            trace.info('LinkPlugin.ExtractContent');
            const extractContentEvent = event as ExtractContentEvent;
            extractContentEvent.content = removeComposeOnlyContent(extractContentEvent.content);
            return;
        } else if (
            event.eventType === PluginEventType.KeyUp ||
            event.eventType === PluginEventType.MouseUp
        ) {
            const shouldNotChangeContextMenu = isFeatureEnabled(
                'doc-attachment-contextualSharingTips'
            );
            const selectionNode = this.editor.getSelectionRange();
            if (selectionNode) {
                const startNode: Node = selectionNode.startContainer;
                const endNode: Node = selectionNode.endContainer;
                const parentNode: Node = startNode.parentNode;
                const anchorElement = parentNode as HTMLAnchorElement;

                if (
                    parentNode.nodeName.toLowerCase() === 'a' &&
                    startNode === endNode &&
                    this.isSharingLink(anchorElement.id, anchorElement.href) &&
                    !!getComposeLinkViewState(anchorElement.id) &&
                    !shouldNotChangeContextMenu
                ) {
                    if (!isContextMenuOpen(anchorElement.id)) {
                        showLinkContextMenu(
                            anchorElement,
                            this.editor,
                            this.linkIdContainer.composeId,
                            this.getRecipientWells(),
                            this.fromAddress
                        );
                    }
                } else {
                    if (!shouldNotChangeContextMenu) {
                        closeLinkContextMenu();
                    }

                    if (startNode === endNode && startNode.nodeName.toLowerCase() === 'a') {
                        const anchor = startNode as HTMLAnchorElement;
                        if (
                            isReadonlyBeautifulLink(anchor) &&
                            selectionNode.startOffset === selectionNode.endOffset
                        ) {
                            // Sometimes cursor is trapped inside the readonly beautiful link, where edit won't work.
                            // So we have to move it outside the readonly link for edit to work
                            if (selectionNode.startOffset !== startNode.childNodes.length) {
                                trace.info('move select before the anchor element.');
                                this.editor.select(anchor, PositionType.Before);
                            } else {
                                trace.info('move select after the anchor element.');
                                this.editor.select(anchor, PositionType.After);
                            }
                        }
                    }
                }
            }
        } else if (event.eventType === PluginEventType.KeyDown) {
            processKeyDownEvent(event, this.utils, this.editor);
        }

        this.processLinkRemovalRelatedEvents(event);
    }

    public insertLinksFromFilePicker(files: CloudFile[]) {
        if (files.length === 0) {
            return;
        }

        if (files[0].providerType === AttachmentDataProviderType.OneDrivePro) {
            // This branch is temp code. The long plan is to go with the else branch
            files.map(file => {
                this.insertContentIntoEditor(file);
            });
        } else {
            insertLinks(
                files,
                format('{0}-{1}', this.id, ++this.insertLinksTaskId),
                this,
                this.linkIdContainer
            );
        }
    }

    private insertContentIntoEditor(file: CloudFile) {
        if (this.editor && !this.editor.isDisposed()) {
            const linkId = OUTLOOK_BEAUTIFIED_PREFIX + getGuid();
            const anchorElement: HTMLAnchorElement = document.createElement('a');
            anchorElement.href = file.location;
            anchorElement.innerText = file.name;
            anchorElement.id = linkId;
            this.editor.insertNode(anchorElement);
            createLoadingLinkView(
                this.editor,
                anchorElement,
                file.location,
                linkId,
                file.name,
                file.type
            );
            processAllLinksInEditor(
                this,
                this.linkIdContainer,
                this.linkIdsWithSpinner,
                false /* shouldChangeLinkId */,
                AnchorElementsSource.InsertLinkFromAttachMenu
            );
        }
    }

    private processLinkRemovalRelatedEvents(event: PluginEvent): void {
        if (isLinkRemovalRelatedEvent(event, this.editor)) {
            this.tryGetAllSPLinks();
        }
    }

    private tryGetAllSPLinks = () => {
        if (!this.pendingSPLinksTask) {
            this.pendingSPLinksTask = setTimeout(() => {
                this.getAllSPLinks();
            }, 500);
        }
    };

    private getAllSPLinks(): void {
        trace.info('processSPLinks');
        this.pendingSPLinksTask = null;
        getAllSharingLinks(this.editor, this.linkIdContainer);
    }

    private onScroll = () => {
        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();
        }
    };

    private isSharingLink(anchorId: string, href: string): boolean {
        if (!anchorId || anchorId === '') {
            return false;
        }

        const sharingLink: SharingLinkInfo = getSharingLinkInfo(anchorId);
        if (sharingLink) {
            return sharingLink.url === href;
        }

        return false;
    }

    public dispose(): void {
        trace.info('LinkPlugin.dispose');
        if (this.onCutDisposer) {
            this.onCutDisposer();
            this.onCutDisposer = null;
        }

        if (this.pendingSPLinksTask) {
            clearTimeout(this.pendingSPLinksTask);
            this.pendingSPLinksTask = null;
        }

        this.editor = null;
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScroll);

        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();
            removeAllComposeLinkViewStates();
        }
    }
}
